import { Option, OptionFolder } from 'api';
import TabPaneFilter from 'components/tabpanefilter';
import { HEADER_HEIGHT } from 'helpers/constants';
import { useSmallScreen } from 'helpers/isSmallScreen';
import Typography from 'helpers/fonts';
import React, { useEffect, useState } from 'react';
import TabPaneCards from './tabpane-cards';
import { useAppState } from 'state/appstate/appstate';
import { useTranslation } from 'react-i18next';
import { useNotificationStore } from '../../state/notificationstate/notificationstate';

export interface TabPaneProps {
  filters?: Array<OptionFolder>;
  description: string;
  title: string;
  options?: Array<Option>;
  optionSettings?: Array<any>;
  runningTotalHeight: number;
}

const TabPane: React.FunctionComponent<TabPaneProps> = ({
  filters,
  options,
  runningTotalHeight,
  title,
}) => {
  const { t } = useTranslation();
  const isSmallScreen = useSmallScreen();
  const { setNotification, messages } = useNotificationStore();
  const maxHeight = isSmallScreen
    ? 'auto'
    : HEADER_HEIGHT + (runningTotalHeight || 0);
  const [selectedFilter, setSelectedFilter] = useState<
    OptionFolder | undefined
    >(filters?.[0]);
  const optionGroups =
    useAppState().styleProduct.style?.optionGroups?.filter(({ id }) =>
      options?.some(
        ({ optionGroupId }) => optionGroupId?.toString() === id.toString()
      )
    ) || [];
  const fullGroups = optionGroups
    .map(({ name, id }) => ({
      title: name,
      options: options?.filter(
        ({ optionGroupId }) => optionGroupId?.toString() === id.toString()
      ),
    }))
    .filter(({ options }) => options?.length);

  const [groups, setGroups] = useState(fullGroups);
  const [showedMessages, setShowedMessages] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!options || !filters || !fullGroups) {
      return;
    }

    setGroups(
      fullGroups?.length
        ? fullGroups
          .map(({ title, options }) => ({
            title,
            options: selectedFilter
              ? options?.filter(
                (o) => o.optionFolderId === selectedFilter?.id
              )
              : options,
          }))
          .filter(({ options }) => options?.length)
        : options.length
          ? [
            {
              title: '',
              options: selectedFilter
                ? options?.filter(
                  (o) => o.optionFolderId === selectedFilter?.id
                )
                : options,
            },
          ]
          : []
    );
  }, [selectedFilter]);

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      <div
        className='invisible-scroll'
        style={{
          display: 'flex',
          width: '100%',
          flexDirection: 'column',
          background: Theme['@grey-lightest'],
          height: `calc(${document.documentElement.style.getPropertyValue('--app-height')} - ${maxHeight}px)`,
          maxHeight: `calc(${document.documentElement.style.getPropertyValue('--app-height')} - ${maxHeight}px)`,
        }}
      >
        {groups?.length ? (
          <>
            <div style={{ gridArea: 'filter', padding: '16px 16px 0' }}>
              {filters && (
                <TabPaneFilter
                  filters={filters}
                  selectFilter={(filter) => {
                    const warning = 'notifications.app.warning.blackBrownMetalVinylOptionPaining';
                    if (
                      filter?.id != selectedFilter?.id &&
                      !messages.some(({ message }) => warning === message) &&
                      (filter?.id === '7' || filter?.id === '8' || filter?.id === '10' || filter?.id === '11') &&
                      filter?.id && !showedMessages[filter.id]
                    ) {
                      setNotification(
                        warning,
                        'warning',
                        'small',
                        false,
                        undefined,
                        'left'
                      );
                      setShowedMessages((value) => ({...value, [filter?.id]: true}))
                    }
                    setSelectedFilter(filter);
                  }}
                  selectedFilter={selectedFilter}
                />
              )}

              {selectedFilter?.description && (
                <div style={{ marginTop: '16px' }}>
                  <Typography styleid={9}>
                    {selectedFilter?.description}
                  </Typography>
                </div>
              )}
            </div>
            {groups &&
              groups.map((group) => (
                <TabPaneCards
                  key={group.title}
                  title={group.title}
                  options={group.options!}
                />
              ))}
          </>
        ) : (
          <div>{t('options.emptyMessage', {title: title.toLowerCase()})}</div>
        )}
      </div>
    </div>
  );
};

export default TabPane;
